import { Avatar, Button, Divider, Typography, useMediaQuery } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Icon from "@mdi/react";
import { mdiLogin, mdiShieldSearch } from "@mdi/js";
import { useNavigate } from "react-router-dom";
import CircleIcon from "@mui/icons-material/Circle";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { borderRadiusStandard } from "../../theme";
import { DEFAULT_SCHOOL_LOGO_URL, HOME_LOGIN_PAGE_URL, LEVELS_TO_COLOR } from "../../utils/Constants";
import { buildSignInPageUrl } from "../../utils/BuildURLs";
import type { Subdomain } from "../../services/backend/types";
import { useBackendServices } from "../../services/backend";

const exams = [
  { level: "C2", label: "Proficiency", color: `${LEVELS_TO_COLOR.C2P}.main` },
  { level: "C1", label: "Advanced", color: `${LEVELS_TO_COLOR.C1A}.main` },
  { level: "B2", label: "First", color: `${LEVELS_TO_COLOR.B2F}.main` },
  { level: "B2", label: "First For Schools", color: `${LEVELS_TO_COLOR.B2S}.main` },
  { level: "B1", label: "Preliminary", color: `${LEVELS_TO_COLOR.B1P}.main` },
  { level: "B1", label: "Preliminary For Schools", color: `${LEVELS_TO_COLOR.B1S}.main` },
  { level: "A2", label: "Key", color: `${LEVELS_TO_COLOR.A2K}.main` },
  { level: "A2", label: "Flyers", color: `${LEVELS_TO_COLOR.A2S}.main` },
  { level: "A2", label: "Key For Schools", color: `${LEVELS_TO_COLOR.A2F}.main` },
  { level: "A1", label: "Movers", color: `${LEVELS_TO_COLOR.A1M}.main` },
  { level: "Pre", label: "Starters", color: `${LEVELS_TO_COLOR.PA1}.main` },
];

const examsByLevel = exams.reduce<Record<string, typeof exams>>((acc, exam) => {
  if (!acc[exam.level]) acc[exam.level] = [];
  acc[exam.level].push(exam);
  return acc;
}, {});

export const HomeLoginPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const signInPageUrl = buildSignInPageUrl();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [backgroundImage, setBackgroundImage] = useState<string>(HOME_LOGIN_PAGE_URL);
  const [logoImage, setLogoImage] = useState<string>(DEFAULT_SCHOOL_LOGO_URL);
  const [subdomain, setSubdomain] = useState("");
  const [schoolName, setSchoolName] = useState<string | undefined>(undefined);
  const { backendService } = useBackendServices();

  const getSubdomainInfoQuery = useQuery<Subdomain, AxiosError>({
    queryKey: ["getSubdomainInfo", subdomain],
    queryFn: () => backendService.getSubdomain({ Name: subdomain }),
    retry: false,
    enabled: subdomain !== "",
  });

  useEffect(() => {
    const parsedData = window.location.host.split(".");
    if (parsedData.length >= 3 || parsedData[1] === "localhost:3000") {
      const subdomain = parsedData[0];
      if (subdomain !== "www") {
        setSubdomain(subdomain);
      }
    }
  }, []);

  useEffect(() => {
    if (getSubdomainInfoQuery.data) {
      if (getSubdomainInfoQuery.data.Background) {
        setBackgroundImage(getSubdomainInfoQuery.data.Background);
      }
      if (getSubdomainInfoQuery.data.Logo) {
        setLogoImage(getSubdomainInfoQuery.data.Logo);
      }
      if (getSubdomainInfoQuery.data.SchoolName) {
        setSchoolName(getSubdomainInfoQuery.data.SchoolName);
      }
    }
  }, [getSubdomainInfoQuery.data]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = backgroundImage;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [backgroundImage]);

  const LogoImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
    <img src={src} alt={alt} style={{ margin: 0, width: "100%", height: "100%", background: "white", objectFit: "contain" }} loading="eager" decoding="async" />
  );

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={12}
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#3A505EE5",
          backgroundPosition: "center",
          opacity: 0.9,
          overflowY: "auto",
          position: "relative",
        }}
      >
        <Grid
          container
          sx={{
            position: isMobile ? "" : "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.9,
            backgroundColor: "#3A505EE5",
            overflowY: "auto",
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              px: 2,
              py: 1,
              color: "white",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "center" : "start",
              justifyContent: "center",
            }}
          >
            <Grid
              xs={12}
              sx={{ color: "white", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", justifyContent: "space-between" }}
            >
              <Grid sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", justifyContent: "start" }}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    m: 1,
                    border: 5,
                    borderColor: "white",
                    bgcolor: "secondary.main",
                    overflow: "hidden",
                  }}
                >
                  <LogoImage src={logoImage || DEFAULT_SCHOOL_LOGO_URL} alt="School logo" />
                </Avatar>
                <Typography variant="h5">{schoolName || t("home_login_page.my_exams_lab")}</Typography>
              </Grid>
              <Button
                onClick={() => navigate(signInPageUrl)}
                startIcon={<Icon path={mdiLogin} size={1} />}
                id="platform-access"
                size="large"
                variant="contained"
                sx={{ ":hover": { background: "#BE563F" }, background: theme.palette.coralOrangeColor.main, borderRadius: borderRadiusStandard }}
              >
                {t("home_login_page.platform_access")}
              </Button>
            </Grid>
          </Grid>
          <Grid sm={12} sx={{ color: "white", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="h4" textAlign="center">
              {t("home_login_page.title_1")}
            </Typography>
            <Typography variant="h4" textAlign="center">
              {t("home_login_page.title_2")}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ color: "white", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            <Grid sm={2} />
            <Grid sm={10} textAlign="center" sx={{ background: "white", borderRadius: borderRadiusStandard, m: 1, p: isMobile ? 1 : 4 }}>
              {Object.entries(examsByLevel).map(([level, exams], index) => (
                <Grid container key={index} sx={{ my: 1 }}>
                  <Grid item xs={12} sm={2} display="flex" justifyContent={isMobile ? "center" : "start"} alignItems="center">
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        height: "50px",
                        width: "50px",
                        background: "#C0E3E5",
                        color: "#417EA0",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {level}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={10}
                    sx={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "start" : "center",
                      justifyContent: ["C2", "C1", "A1", "Pre A1"].includes(level) ? "center" : "space-between",
                    }}
                  >
                    {isMobile ? (
                      exams.map((exam, examIndex) => (
                        <Grid item xs={12} display="flex" justifyContent="start" key={examIndex}>
                          <Typography variant="h6" sx={{ px: 1, color: "#417EA0", fontWeight: "bold", alignItems: "center", display: "flex" }}>
                            <CircleIcon sx={{ color: exam.color, paddingRight: 0.5, marginRight: 0.5 }} />
                            {exam.label}
                          </Typography>
                        </Grid>
                      ))
                    ) : exams.length === 1 ? (
                      <Grid container>
                        <Grid item xs={4} />
                        <Grid item xs={4} display="flex" justifyContent="start">
                          <Typography variant="h6" sx={{ color: "#417EA0", fontWeight: "bold", alignItems: "center", display: "flex" }}>
                            <CircleIcon sx={{ color: exams[0].color, paddingRight: 0.5, marginRight: 0.5 }} />
                            {exams[0].label}
                          </Typography>
                        </Grid>
                        <Grid item xs={4} />
                      </Grid>
                    ) : exams.length === 2 ? (
                      <Grid container>
                        <Grid item xs={4} display="flex" justifyContent="start">
                          <Typography variant="h6" sx={{ color: "#417EA0", fontWeight: "bold", alignItems: "center", display: "flex" }}>
                            <CircleIcon sx={{ color: exams[0].color, paddingRight: 0.5, marginRight: 0.5 }} />
                            {exams[0].label}
                          </Typography>
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={4} display="flex" justifyContent="start">
                          <Typography variant="h6" sx={{ color: "#417EA0", fontWeight: "bold", alignItems: "center", display: "flex" }}>
                            <CircleIcon sx={{ color: exams[1].color, paddingRight: 0.5, marginRight: 0.5 }} />
                            {exams[1].label}
                          </Typography>
                        </Grid>
                      </Grid>
                    ) : (
                      exams.map((exam, examIndex) => (
                        <Grid item xs={4} display="flex" justifyContent="start" key={examIndex}>
                          <Typography variant="h6" sx={{ color: "#417EA0", fontWeight: "bold", alignItems: "center", display: "flex" }}>
                            <CircleIcon sx={{ color: exam.color, paddingRight: 0.5, marginRight: 0.5 }} />
                            {exam.label}
                          </Typography>
                        </Grid>
                      ))
                    )}
                  </Grid>
                  {index < Object.entries(examsByLevel).length - 1 && (
                    <Grid item xs={12} sm={12}>
                      <Divider sx={{ width: "100%", mt: 1, border: "1px dashed #417EA0" }} />
                    </Grid>
                  )}
                </Grid>
              ))}
            </Grid>
            <Grid sm={2} />
          </Grid>
          <Grid item xs={12} sx={{ px: 2, py: 1, color: "white", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            <Button
              startIcon={<Icon path={mdiShieldSearch} size={1} />}
              size="large"
              variant="outlined"
              color="inherit"
              sx={{ ":hover": { background: "white", color: "#40505B" }, border: 1, color: "white", borderRadius: borderRadiusStandard }}
              onClick={() => navigate("/privacy-policy")}
            >
              {t("home_login_page.privacy_policy")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
